<!--
  A full-width row of its own beneath the scene, not a column inside it — see
  the component's note. Collapsed, it is one read-only summary line from the
  SAVED snapshot; the control right after its label opens the form beneath
  that line, in the same row. The summary line never moves when the form opens,
  so the control stays under the pointer.

  Hover targets teach without a dialog: the row's label says what the fields
  are for, and each tag chip shows the filter that finds it again in Archive.
-->
<td class="scene-tags-cell" colspan="8">
  <!-- `covered`: only a scene of a sensor Add to STAC is switched on for (see the component's note). -->
  <div class="scene-meta" *ngIf="saved !== null && covered" [class.is-unavailable]="unavailable" [class.is-open]="expanded">
    <div class="scene-meta-summary">
      <!-- Names the row. It explains and does nothing else, so it is NOT a
           button: `tabindex` earns it the tooltip on keyboard focus, and the
           aria-label carries the same sentence to a reader. -->
      <span
        class="scene-tags-label"
        tabindex="0"
        role="img"
        [attr.aria-label]="unavailable ? UNAVAILABLE_TOOLTIP : INFO_TOOLTIP"
        [matTooltip]="unavailable ? UNAVAILABLE_TOOLTIP : INFO_TOOLTIP"
        matTooltipClass="scene-tags-tooltip"
        matTooltipPosition="above"
      >
        <mat-icon class="material-icons-outlined">local_offer</mat-icon>
        <span class="scene-tags-label-text">Your metadata</span>
      </span>

      <!-- Right after the label, not at the line's far end: on a narrow screen the scene table can
           be wider than the panel, which hides its right-hand side. -->
      <button
        type="button"
        class="scene-meta-toggle"
        [disabled]="unavailable || saving"
        [attr.aria-expanded]="expanded"
        [attr.aria-label]="expanded ? 'Close your metadata for this scene' : 'Edit your metadata for this scene'"
        (click)="toggle()"
      >
        <mat-icon>{{ expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>

      <ng-container *ngIf="!unavailable; else notInArchive">
        <ng-container *ngFor="let entry of summary; let last = last; trackBy: trackEntry">
          <ng-container *ngIf="entry.tags; else valueEntry">
            <span
              *ngFor="let tag of entry.tags"
              class="scene-meta-chip"
              [matTooltip]="entry.field.title"
              matTooltipClass="scene-tags-tooltip"
            >{{ tag }}</span>
          </ng-container>
          <ng-template #valueEntry>
            <span class="scene-meta-value" [matTooltip]="entry.field.title" matTooltipClass="scene-tags-tooltip">{{ entry.text }}</span>
          </ng-template>
          <span class="scene-meta-sep" *ngIf="!last" aria-hidden="true">·</span>
        </ng-container>
        <span class="scene-meta-empty" *ngIf="summary.length === 0">None yet</span>
      </ng-container>
      <ng-template #notInArchive>
        <span class="scene-meta-empty">Not in the Data Catalog</span>
      </ng-template>
    </div>

    <!-- The form: one widget per field, as the definitions describe it. -->
    <div class="scene-meta-form" *ngIf="expanded && draft">
      <div class="scene-meta-field" *ngFor="let field of fields; trackBy: trackField" [attr.data-member]="field.member">
        <label class="scene-meta-field-title" [attr.for]="inputId(field)">{{ field.title }}</label>
        <div class="scene-meta-field-input" *ngIf="isKnown(field); else notRead" [ngSwitch]="field.kind">
          <mat-chip-list *ngSwitchCase="'tagset'" #chipList class="scene-tags-list" [attr.aria-label]="field.title">
            <mat-chip
              *ngFor="let tag of tagsOf(field)"
              class="scene-tag"
              [removable]="!saving"
              (removed)="removeTag(field, tag)"
            >
              {{ tag }}
              <mat-icon matChipRemove *ngIf="!saving" [attr.aria-label]="'Remove ' + tag">cancel</mat-icon>
            </mat-chip>
            <span class="scene-tags-add" [class.is-disabled]="saving || atLimit(field)">
              <mat-icon class="scene-tags-add-icon material-icons-outlined">add</mat-icon>
              <input
                class="scene-tags-input"
                placeholder="add tag"
                [id]="inputId(field)"
                [attr.maxlength]="field.maxLength"
                [disabled]="saving || atLimit(field)"
                [matChipInputFor]="chipList"
                [matChipInputSeparatorKeyCodes]="separatorKeys"
                [matChipInputAddOnBlur]="true"
                (matChipInputTokenEnd)="addTag(field, $event)"
                (input)="hints[field.member] = ''"
              />
            </span>
          </mat-chip-list>

          <!-- The app's outlined field, as the Reports dialog's and the task forms' are. -->
          <mat-form-field *ngSwitchCase="'text'" appearance="outline" class="scene-meta-field-control">
            <input
              matInput
              type="text"
              class="scene-meta-text"
              [id]="inputId(field)"
              [value]="valueOf(field)"
              [attr.maxlength]="field.maxLength"
              [disabled]="saving"
              (input)="setValue(field, $any($event.target).value)"
            />
          </mat-form-field>

          <mat-form-field *ngSwitchCase="'enum'" appearance="outline" class="scene-meta-field-control">
            <mat-select
              class="scene-meta-select"
              [id]="inputId(field)"
              [value]="valueOf(field)"
              [disabled]="saving"
              [disableOptionCentering]="true"
              panelClass="matopenpurpose"
              (selectionChange)="setValue(field, $event.value)"
            >
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let value of field.values" [value]="value">{{ labelOf(field, value) }}</mat-option>
              <!-- A level stored under newer definitions than this tab's: shown as stored. -->
              <mat-option *ngIf="unlistedLevel(field) as level" [value]="level">{{ level }}</mat-option>
            </mat-select>
          </mat-form-field>

          <small class="scene-meta-description" *ngIf="field.description">{{ field.description }}</small>
          <!-- Always present, so a hint that appears is announced. -->
          <small class="scene-tags-hint" role="status" aria-live="polite">{{ hints[field.member] }}</small>
        </div>
        <ng-template #notRead>
          <div class="scene-meta-field-input">
            <span class="scene-meta-description">Added since this page loaded — reload the page to edit it.</span>
          </div>
        </ng-template>
      </div>

      <div class="scene-meta-actions">
        <small class="scene-tags-hint scene-meta-error" role="status" aria-live="polite">{{ formError }}</small>
        <ng-container *ngIf="confirming; else actions">
          <span class="scene-meta-confirm">Discard changes?</span>
          <button mat-stroked-button type="button" class="scene-meta-button scene-meta-keep" (click)="keepEditing()">Keep editing</button>
          <button mat-stroked-button type="button" class="scene-meta-button scene-meta-discard" (click)="discard()">Discard</button>
        </ng-container>
        <ng-template #actions>
          <!-- Styled as the Reports button and its dialog's Close: the app's stroked button beside
               action-button submit, at the Reports button's size. -->
          <button mat-stroked-button type="button" class="scene-meta-button scene-meta-cancel" [disabled]="saving" (click)="cancel()">Cancel</button>
          <button mat-raised-button type="button" class="action-button submit scene-meta-button scene-meta-save" [disabled]="!canSave" (click)="save()">
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </ng-template>
      </div>
    </div>
  </div>
</td>
